// warehouseTables — "somebody is looking at the warehouse pipeline" registry.
//
// REALTIME_REDESIGN_PLAN.md §4 step 1: the four warehouse tables (inbound_lots,
// lot_assignments, production_runs, lot_returns) are whole-table follows that
// grow forever, and until now every admin, manager and packaging-house tab
// bound all four at app start — four more policy checks per change per tab,
// for pages most of those tabs never open. Now a warehouse page declares
// itself with `useWarehouseTables()` while mounted, and AppContext binds the
// tables only while at least one such page is on screen (ref-counted).
//
// This lives outside AppContext on purpose: pages import the hook from here,
// so a page test that mocks `../context/AppContext` keeps working, and the
// registry has no React state of its own — AppProvider subscribes to it.
import { useEffect } from 'react';

let holders = 0;
const listeners = new Set<(wanted: boolean) => void>();
let lastWanted = false;
let releaseTimer: ReturnType<typeof setTimeout> | null = null;

/** The last page leaving keeps the tables bound this long, so a hop between
 *  two warehouse pages (the next chunk is still loading when the first page
 *  unmounts) or a quick Settings-and-back does not leave four channels and
 *  re-download four whole tables. */
export const WAREHOUSE_RELEASE_GRACE_MS = 1500;

function notify(): void {
  const wanted = holders > 0;
  if (wanted === lastWanted) return;
  lastWanted = wanted;
  for (const l of [...listeners]) { try { l(wanted); } catch { /* ignore */ } }
}

/** Hold the warehouse tables open. Returns release(); releasing twice is a no-op. */
export function acquireWarehouseTables(): () => void {
  holders += 1;
  if (releaseTimer) { clearTimeout(releaseTimer); releaseTimer = null; }
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders = Math.max(0, holders - 1);
    if (holders > 0) return;
    if (releaseTimer) clearTimeout(releaseTimer);
    releaseTimer = setTimeout(() => { releaseTimer = null; notify(); }, WAREHOUSE_RELEASE_GRACE_MS);
  };
}

/** True while at least one warehouse page is mounted. */
export function warehouseTablesWanted(): boolean {
  return holders > 0;
}

/** AppProvider's side: told whenever wanted flips (and on every acquire/release). */
export function onWarehouseTablesWanted(cb: (wanted: boolean) => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Call at the top of any page or panel that reads inboundLots /
 *  lotAssignments / productionRuns / lotReturns from the app context. */
export function useWarehouseTables(): void {
  useEffect(() => acquireWarehouseTables(), []);
}

/** Test seam. */
export function _resetWarehouseTables(): void {
  holders = 0;
  lastWanted = false;
  if (releaseTimer) clearTimeout(releaseTimer);
  releaseTimer = null;
  listeners.clear();
}
